import { useCallback, useEffect } from 'react';

import { useGlobalState } from '@providers/global-context-provider';

import {
  MetadataSyncResult,
  MetadataSyncType,
  SalesforceMetadataSyncResult,
  CustomObjectDownloadStatus,
} from '@modules/salesforce/models';

import {
  SalesforceConnectionsService,
  SalesforceMetadataSyncResultsService,
} from '@modules/salesforce/services';

import { usePolling } from './use-polling';
import {
  RefreshModalScreens,
  useSFConnectionState,
} from '@contexts/sf-connection-context.js';

type REFRESH_REQUEST_TYPE = 'ACTION' | 'ON_LOAD';

/**
 * useMetadataRefresh is a custom hook that manages the metadata synchronization process
 * and its state, including initiating sync, polling for status updates, and updating the global state.
 *
 * @param {number} connectionId - The ID of the Salesforce connection for which metadata sync needs to be managed.
 * @returns {Object} - An object containing the handleMetadataRefresh function to manually trigger a metadata refresh.
 */
const useMetadataRefresh = (connectionId) => {
  const {
    refreshingMetadataConnectionId,
    handleStartRefreshingMetadata,
    handleStopRefreshingMetadata,
    handleCustomObjectSyncing,
    isCustomObjectSynced,
  } = useGlobalState();

  const { handleUpdateOverlayMode, handleUpdateRefreshModalScreen } =
    useSFConnectionState();

  // Add cleanup effect when connectionId changes
  // useEffect(() => {
  //   // Close the modal when switching to a different connection/version
  //   handleUpdateOverlayMode('none');
  // }, [connectionId, handleUpdateOverlayMode]);

  /**
   * Checks for metadata sync status.
   * @param connectionId metadataConnection Id
   * @param callback function to call after api result
   */
  const fetchRefreshMetadataSyncResults = (connectionId, callback?) => {
    const query = `,salesforceConnectionId:${connectionId},metadataSyncType:${MetadataSyncType.UPDATE_SYNC}`;
    const sort = 'id,desc';
    SalesforceMetadataSyncResultsService.findAll(
      query,
      undefined,
      undefined,
      sort,
    ).then(callback);
  };

  const fetchSyncStatus = useCallback(() => {
    if (!connectionId) return;

    fetchRefreshMetadataSyncResults(connectionId, (data) => {
      const lastSyncData = data?.content?.[0] ?? {};
      const {
        metadataSyncResult,
        salesforceConnectionId,
        customObjectDownloadStatus,
      } = lastSyncData;

      handleRefreshGlobalState(
        metadataSyncResult,
        salesforceConnectionId,
        customObjectDownloadStatus,
      );
    });
  }, [
    connectionId,
    handleStartRefreshingMetadata,
    handleStopRefreshingMetadata,
  ]);

  const { startPolling, stopPolling } = usePolling(fetchSyncStatus, 5000);

  // Check sync status when the hook mounts or connectionId changes
  useEffect(() => {
    if (connectionId) {
      fetchSyncStatus();
    }
  }, [connectionId, fetchSyncStatus]);

  /*
    Method: handleRefreshGlobalState
    This Global State holds IS_METADATA_REFRESHING flag based on this we are showing button
    status in both side nav and connection page
    */
  const handleRefreshGlobalState = useCallback(
    (
      result: string,
      salesforceConnectionId?: number,
      customObjectDownloadStatus?: CustomObjectDownloadStatus,
      showOverlay: boolean = false,
    ) => {
      if (
        result == MetadataSyncResult.IN_PROGRESS ||
        result == MetadataSyncResult.NOT_STARTED
      ) {
        handleStartRefreshingMetadata(salesforceConnectionId);
        if (customObjectDownloadStatus == CustomObjectDownloadStatus.SUCCESS) {
          handleCustomObjectSyncing(true);
        }
        if (salesforceConnectionId === connectionId && showOverlay) {
          handleUpdateOverlayMode('refresh_metadata_screen');
        }
        startPolling();
      } else {
        handleStopRefreshingMetadata(salesforceConnectionId);
        handleCustomObjectSyncing(false);
        if (salesforceConnectionId === connectionId) {
          handleUpdateOverlayMode('none');
        }
        stopPolling();
      }
    },
    [
      handleStartRefreshingMetadata,
      handleStopRefreshingMetadata,
      handleUpdateOverlayMode,
      stopPolling,
      handleCustomObjectSyncing,
      connectionId,
    ],
  );

  const handleStartMetaDataRefresh = useCallback(
    (
      syncResult: SalesforceMetadataSyncResult,
      refreshRequestType: REFRESH_REQUEST_TYPE,
    ) => {
      const result = syncResult?.metadataSyncResult;
      const customObjectDownloadStatus = syncResult?.customObjectDownloadStatus;
      const salesforceConnectionId = syncResult?.salesforceConnectionId;

      // If sync is already requested
      if (
        result == MetadataSyncResult.IN_PROGRESS ||
        result == MetadataSyncResult.NOT_STARTED
      ) {
        handleRefreshGlobalState(
          result,
          salesforceConnectionId,
          customObjectDownloadStatus,
          true,
        );
        handleUpdateOverlayMode('refresh_metadata_screen');
        if (salesforceConnectionId === connectionId) {
          if (refreshRequestType === 'ACTION') {
            if (
              customObjectDownloadStatus == CustomObjectDownloadStatus.SUCCESS
            ) {
              handleUpdateRefreshModalScreen(
                RefreshModalScreens.SYNC_PARTIALLY_TRIGGERED,
              );
            } else {
              handleUpdateRefreshModalScreen(
                RefreshModalScreens.SYNC_ALREADY_TRIGGERED,
              );
            }
          } else {
            if (
              customObjectDownloadStatus == CustomObjectDownloadStatus.SUCCESS
            ) {
              handleUpdateRefreshModalScreen(
                RefreshModalScreens.SYNC_PARTIALLY_TRIGGERED,
              );
            } else {
              handleUpdateRefreshModalScreen(
                RefreshModalScreens.SYNC_TRIGGERED,
              );
            }
          }
        }
      } else {
        // makes a sync metadata request if not already requested
        if (salesforceConnectionId === connectionId) {
          handleUpdateOverlayMode('refresh_metadata_screen');
          handleUpdateRefreshModalScreen(RefreshModalScreens.SYNC_TRIGGERED);
        }
        SalesforceConnectionsService.syncMetadata(connectionId).then(
          (res: SalesforceMetadataSyncResult) => {
            handleRefreshGlobalState(
              res?.metadataSyncResult,
              res?.salesforceConnectionId,
              res?.customObjectDownloadStatus,
              true, // Show overlay on initial refresh
            );
          },
        );
      }
    },
    [
      connectionId,
      handleRefreshGlobalState,
      handleUpdateOverlayMode,
      handleUpdateRefreshModalScreen,
    ],
  );

  const handleMetadataRefresh = useCallback(
    (refreshRequestType: REFRESH_REQUEST_TYPE) => {
      fetchRefreshMetadataSyncResults(connectionId, (data) => {
        handleStartMetaDataRefresh(data?.content?.[0], refreshRequestType);
        startPolling();
      });
    },
    [connectionId, handleStartMetaDataRefresh, startPolling],
  );

  return {
    handleMetadataRefresh,
    refreshingMetadataConnectionId,
  };
};

export { useMetadataRefresh };
